<template>
  <div id="FlyInWording"/>
</template>

<script setup>
import p5 from 'p5'
import {ref, reactive, watchEffect, onUnmounted, watch, onMounted} from 'vue'

import defaultValue from "@/utils/defaultValue"
import colorTool from "@/utils/colorTool"
import freeImg from "@/utils/freeImg";

const {freeImages} = freeImg()
const {defaultWidth, defaultHeight} = defaultValue()


const props = defineProps(['effectTheme', 'controlMode'])
const {changeToRgb, rgbToHsb} = colorTool()


const effectThemeData = reactive(props.effectTheme)
let canvasWrap = ref('stop')
let backgroundHsbColor = ref([0,0,0])
let backgroundColor = ref('rgb(0,0,0)')
let imgUrl = ref(null)
let imagePathType = ref('')
let drawPot = ref(0)
let animateSpeed = ref(3)
let animateTextArr = reactive({arr: []})
let backgroundImage = ref('')

const flyInWordingAnimate = (sketch) => {
    let cw = defaultWidth.value
    let ch = defaultHeight.value
  // Global variables
  let particles = [];
  let pixelSteps = 5; // 要跳過的像素數
  let words = [];
  let wordIndex = 0;
  let bg
  let vid

  class Particle {
    constructor() {
      this.pos = sketch.createVector(0, 0);
      this.vel = sketch.createVector(0, 0);
      this.acc = sketch.createVector(0, 0);
      this.target = sketch.createVector(0, 0);

      this.closeEnoughTarget = 260;
      this.maxSpeed = 22.0;
      this.maxForce = 0.1;
      this.particleSize = 3;
      this.isKilled = false;

      this.startColor = sketch.color(0);
      this.targetColor = sketch.color(0);
      this.colorWeight = 0;
      this.colorBlendRate = 0.025;
    }

    move() {
      // Check if particle is close enough to its target to slow down
      let proximityMult = 1.0;
      let distance = sketch.dist(this.pos.x, this.pos.y, this.target.x, this.target.y);
      if (distance < this.closeEnoughTarget) proximityMult = distance / this.closeEnoughTarget

      // Add force towards target
      let towardsTarget = sketch.createVector(this.target.x, this.target.y);
      towardsTarget.sub(this.pos);
      towardsTarget.normalize();
      towardsTarget.mult(this.maxSpeed * proximityMult);

      let steer = sketch.createVector(towardsTarget.x, towardsTarget.y);
      steer.sub(this.vel);
      steer.normalize();
      steer.mult(this.maxForce);
      this.acc.add(steer);

      // 移動粒子
      this.vel.add(this.acc);
      this.pos.add(this.vel);
      this.acc.mult(0);
    }

    draw() {
      // Draw particle
      let currentColor = sketch.lerpColor(this.startColor, this.targetColor, this.colorWeight);
      sketch.noStroke();
      sketch.fill(currentColor);
      sketch.ellipse(this.pos.x, this.pos.y, this.particleSize, this.particleSize);

      // Blend towards its target color
      if (this.colorWeight < 1.0) {
        this.colorWeight = sketch.min(this.colorWeight + this.colorBlendRate, 1.0);
      }
    }

    kill() {
      if (!this.isKilled) {
        // Set its target outside the scene
        let randomPos = generateRandomPos(sketch.floor(sketch.width / 2),  sketch.floor(sketch.height / 2), sketch.floor((sketch.width + sketch.height) / 2));
        this.target.x = randomPos.x;
        this.target.y = randomPos.y;

        // Begin blending its color to black
        this.startColor = sketch.lerpColor(this.startColor, this.targetColor, this.colorWeight);
        this.targetColor = sketch.color(0);
        this.colorWeight = 0;

        this.isKilled = true;
      }
    }
  }
  // Picks a random position from a point's radius
  function generateRandomPos(x, y, mag) {
    let randomDir = sketch.createVector(sketch.floor(sketch.random(0, sketch.width)) , sketch.floor(sketch.random(0, sketch.height)));
    let pos = sketch.createVector(x, y);
    pos.sub(randomDir);
    pos.normalize();
    pos.mult(mag);
    pos.add(x, y);

    return pos;
  }

  // Makes all particles draw the next word
  function nextWord(word) {
    // Draw word in graphics 緩衝區
    let pg = sketch.createGraphics(cw, ch);
    pg.pixelDensity(1);
    pg.strokeWeight(1);
    pg.stroke(255);
    pg.fill(255); // textColor
    pg.textSize(75);
    pg.textAlign(sketch.CENTER);
    pg.textStyle(sketch.BOLD);
    pg.text(word, pg.width/2, pg.height/2 + 20);
    // loadPixels
    pg.loadPixels()

    // Next color for all pixels to change to
    // let newColor = sketch.color(sketch.random(0, 255), sketch.random(0, 255), sketch.random(0, 255));
    let newColorH = sketch.abs(sketch.random(0, 360))
    if (sketch.float(newColorH) === sketch.float(backgroundHsbColor[0])) {
      newColorH = sketch.abs((newColorH + 15) / 360)
    }
    let newColorS = sketch.abs(sketch.random(40, 100))
    let newColor = sketch.color(newColorH, newColorS, 100);
    let particleCount = particles.length;
    let particleIndex = 0;

    // 使用 巢狀 去算 x座標, y座標
    for (let y2 = 0; y2 < pg.height; y2 += pixelSteps) {
      for (let x2 = 0; x2 < pg.width; x2 += pixelSteps) {

        // p 會為 一個 rgba [0,0,0,0]
        // 比對 p點
        let p = pg.get(x2, y2)

        if (p[0] !== 0 || p[1] !== 0 || p[2] !== 0) {
          let newParticle;
          newParticle = new Particle();

          let randomPos = generateRandomPos(sketch.floor(sketch.width / 2), sketch.floor(sketch.height / 2), sketch.floor((sketch.width + sketch.height) / 2));
          newParticle.pos.x = randomPos.x;
          newParticle.pos.y = randomPos.y;

          newParticle.maxSpeed = sketch.random(20.0, 25.0);
          newParticle.maxForce = newParticle.maxSpeed * 0.025;
          newParticle.particleSize = sketch.random(3, 5);
          newParticle.colorBlendRate = sketch.random(0.0025, 0.03);

          newParticle.startColor = sketch.lerpColor(newParticle.startColor, newParticle.targetColor, newParticle.colorWeight);
          newParticle.targetColor = newColor;
          newParticle.colorWeight = 0;

          newParticle.target.x = x2;
          newParticle.target.y = y2;

          // update particles
          particles.push(newParticle);

        }
      }
    }

    // remove pg
    pg.remove()


    // 切換時, 清空畫面
    if (particleIndex < particleCount) {
      for (let i = particleIndex; i < particleCount; i++) {
        let particle = particles[i];
        particle.kill();
      }
    }

  }

    sketch.setup = () => {
      sketch.pixelDensity(1);
      sketch.createCanvas(cw, ch);
      sketch.colorMode(sketch.HSB, 360, 100, 100, 255)
      bg = ''
      vid = ''

      if (
          imagePathType.value !== 'gif' &&
          imagePathType.value !== 'mp4' &&
          imagePathType.value !== 'webm' &&
          imgUrl.value &&
          imgUrl.value !== null &&
          imgUrl.value !== ''
      ) bg = sketch.loadImage(imgUrl.value)
      // 若 imgUrl.value .gif
      if (imagePathType.value.toString() === 'gif' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== '') {
        bg = sketch.createImg(imgUrl.value)
        bg.style('background-color', `${backgroundColor.value}`)
        bg.style('z-index', '-1')
        // 設置 背景圖 position
        bg.size(cw, ch)
        bg.position(0,0)
        // 將 canvas background 切換為 透明
        sketch.background(255, 255, 255, 0)
      }
      // 若 imgUrl.value .mp4 or .webm
      else if (
          imagePathType.value === 'mp4' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== '' ||
          imagePathType.value === 'webm' && imgUrl.value && imgUrl.value !== null && imgUrl.value !== ''
      ) {
        // mp4
        vid = sketch.createVideo(imgUrl.value)
        vid.style('background-color', `${backgroundColor.value}`)
        vid.style('z-index', '-1')
        // mp4
        vid.size(sketch.width, sketch.height);
        vid.position(0,0)
        vid.volume(0);
        vid.loop()
        sketch.background(backgroundColor.value)
      }

      // init
      drawPot.value = 0
      wordIndex = 0
      words = []
      particles = []

      if (animateTextArr.arr && animateTextArr.arr.length > 0) {
        words = animateTextArr.arr; // 將您想使用的文字加入此處
        if (words.length > 0) nextWord(words[wordIndex]);
        else words = []
      } else words = []
    }

    sketch.draw = () => {
      // Background & motion blur
      drawPot.value ++
      // 3*60 update
      if (
          drawPot.value > 1 &&
          drawPot.value % sketch.abs(animateSpeed.value*60) === 0
      ) {
        wordIndex++
        if (words && words.length > 0) {
          if (wordIndex > words.length - 1) {
            wordIndex = 0
            // 清空畫面
            for (let i = particles.length; i < particles.length; i++) {
              let particle = particles[i];
              particle.kill();
            }
            // init particles
            particles = []
            drawPot.value = 0
          }
          nextWord(words[wordIndex])
        }
      }
      sketch.clear();
      // 若 imgUrl.value 不是 .gif or .mp4
      if (
          imagePathType.value !== 'gif' &&
          imagePathType.value !== 'mp4' &&
          imagePathType.value !== 'webm'
      ) {
        // 添加 背景色
        sketch.fill(backgroundColor.value);
        sketch.noStroke();
        sketch.rect(0, 0, sketch.width * 2, sketch.height * 2);
        if (
            imgUrl.value &&
            imgUrl.value !== null &&
            imgUrl.value !== ''
        ) sketch.image(bg, 0, 0, sketch.width, sketch.height)
      }


      if (words && words.length  <= 0) {
        sketch.noLoop()
        return
      }

      for (let x = particles.length - 1; x > -1; x--) {
        let particle = particles[x];
        particle.move();
        particle.draw();
      }
    }
}

function initAnimate () {
  if (canvasWrap.value !== 'stop') {
    canvasWrap.value.remove()
    canvasWrap.value = 'stop'
  }
}

// watch(drawPot, (n, o) => {
//   if (n > 18000) { // 5min init
//     initAnimate()
//     canvasWrap.value = new p5(flyInWordingAnimate, 'FlyInWording')
//     drawPot.value = 0
//   }
// })

watchEffect(() => {
  const {
    Speed: wordLoopSpeed,
    BackgroundColor,
    BackgroundImage,
    AnimateText
  } = effectThemeData

  // 背景色
  let bgColor = typeof BackgroundColor === "object" && BackgroundColor.length > 0 ? changeToRgb(BackgroundColor) : 'rgb(0,0,0)'
  let bgHsbColor = typeof BackgroundColor === "object" && BackgroundColor.length > 0 ? rgbToHsb(BackgroundColor) : [0,0,0]

  // 文字
  let filterTexts = AnimateText.filter(text => text !== "").length > 0 ? AnimateText.filter(text => text !== '') : []
  // 背景色, 背景圖, 文字 需重置動畫
  if (
      backgroundImage.value !== BackgroundImage ||
      backgroundColor.value !== bgColor ||
      backgroundHsbColor.value[0] !== bgHsbColor[0] ||
      backgroundHsbColor.value[1] !== bgHsbColor[1] ||
      backgroundHsbColor.value[2] !== bgHsbColor[2] ||
      animateTextArr.arr !== filterTexts
  ) {
    imagePathType.value = ''
    backgroundImage.value = BackgroundImage
    backgroundColor.value = bgColor
    backgroundHsbColor.value = bgHsbColor
    animateTextArr.arr = filterTexts
    if (backgroundImage.value !== '') {
      if (props.controlMode && props.controlMode.toString() === 'true') {
        const pathSplit = backgroundImage.value.split('.')
        imgUrl.value = freeImages[pathSplit[0]]
        imagePathType.value = pathSplit[1]
      } else {
        imgUrl.value = backgroundImage.value.split('#')[0]
        if (imgUrl.value.endsWith('.mp4')) imagePathType.value = 'mp4'
        else if (imgUrl.value.endsWith('.webm')) imagePathType.value = 'webm'
        else if (imgUrl.value.endsWith('.gif')) imagePathType.value = 'gif'
      }

    }
    if (backgroundImage.value === '') {
      imgUrl.value = ''
      imagePathType.value = ''
    }
    // create p5
    if (canvasWrap.value !== 'stop') {
      initAnimate()
      canvasWrap.value = new p5(flyInWordingAnimate, 'FlyInWording')
    }
  }

  // 文字切換速度 可以動態更新
  if (wordLoopSpeed && wordLoopSpeed > 0) animateSpeed.value = wordLoopSpeed
})

onMounted(() => {
  if (canvasWrap.value === 'stop') canvasWrap.value = new p5(flyInWordingAnimate, 'FlyInWording')
})

onUnmounted(() => {
  drawPot.value = 0
  imgUrl.value = ''
  imagePathType.value = ''
  backgroundHsbColor.value = [0,0,0]
  backgroundColor.value = 'rgb(0,0,0)'
  animateTextArr.arr = []
  animateSpeed.value = 3
  initAnimate()
})

</script>

<style lang="scss" scoped></style>
